import { useState } from 'react';
import { Link } from 'react-router-dom';
import CartButton from '../../../components/CartButton';
import WishlistButton from '../../../components/WishlistButton';

interface VideoGalleryNavbarProps {
  scrolled: boolean;
}

export default function VideoGalleryNavbar({ scrolled }: VideoGalleryNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-stone-950/95 backdrop-blur-xl shadow-2xl shadow-black/20 py-3' 
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4 cursor-pointer group">
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
                alt="House of FMR"
                className="h-14 w-14 object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-amber-400/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div className="hidden sm:block">
              <p className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300 font-display text-xl tracking-wide">House of FMR</p>
              <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-medium">Events & Styling</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className="relative px-4 py-2 text-sm font-medium text-white/70 hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap group"
            >
              Home
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link
              to="/#classes"
              className="relative px-4 py-2 text-sm font-medium text-white/70 hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap group"
            >
              Classes
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link
              to="/video-gallery"
              className="relative px-4 py-2 text-sm font-medium text-amber-300 transition-colors cursor-pointer whitespace-nowrap group"
            >
              Video Gallery
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent"></span>
            </Link>
            <Link
              to="/gallery"
              className="relative px-4 py-2 text-sm font-medium text-white/70 hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap group"
            >
              Photo Gallery
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent group-hover:w-full transition-all duration-300"></span>
            </Link>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <WishlistButton />
            <CartButton />
            <Link
              to="/#booking"
              className="px-7 py-3 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white cursor-pointer border border-white/10"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 bg-stone-950/98 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
            <div className="space-y-1">
              <Link
                to="/"
                className="block w-full text-left px-4 py-3 text-sm font-medium text-white/80 hover:text-amber-300 hover:bg-white/5 rounded-xl transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/#classes"
                className="block w-full text-left px-4 py-3 text-sm font-medium text-white/80 hover:text-amber-300 hover:bg-white/5 rounded-xl transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Classes
              </Link>
              <Link
                to="/video-gallery"
                className="block w-full text-left px-4 py-3 text-sm font-medium text-amber-300 bg-white/5 rounded-xl cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Video Gallery
              </Link>
              <Link
                to="/gallery"
                className="block w-full text-left px-4 py-3 text-sm font-medium text-white/80 hover:text-amber-300 hover:bg-white/5 rounded-xl transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Photo Gallery
              </Link>
              <Link
                to="/wishlist"
                className="block w-full text-left px-4 py-3 text-sm font-medium text-white/80 hover:text-amber-300 hover:bg-white/5 rounded-xl transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-heart-3-line mr-2"></i>
                Wishlist
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
